import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, CheckCircle2, Phone, Clock, MapPin } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { INQUIRY_TYPES, COMPANY } from "../data/site";
import { api, formatApiErrorDetail } from "../lib/api";

const TYPE_MAP = {
  estimate: "見積依頼",
  recruit: "採用の問い合わせ",
  realestate: "不動産相談",
  partner: "協力会社",
  work: "工事依頼",
};

const INITIAL = { category: "工事依頼", name: "", company: "", email: "", phone: "", message: "" };

const inputCls =
  "w-full border border-line bg-ink px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-gray-600 focus:border-accent";

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [contact, setContact] = useState({
    zip: COMPANY.zip,
    address: COMPANY.address,
    tel: COMPANY.tel,
    fax: COMPANY.fax,
    hours: COMPANY.hours,
    holiday: COMPANY.holiday,
  });

  useEffect(() => {
    api
      .get("/company")
      .then((r) => {
        if (r.data?.contact) setContact((c) => ({ ...c, ...r.data.contact }));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = params.get("type");
    if (t && TYPE_MAP[t]) setForm((f) => ({ ...f, category: TYPE_MAP[t] }));
  }, [params]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="GET IN TOUCH"
        titleEn="CONTACT"
        titleJa="お問い合わせ"
        desc="工事依頼・見積・不動産相談・協力会社・採用に関するお問い合わせはこちらから。"
      />
      <section className="border-t border-line py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-16 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-7">
            {sent ? (
              <Reveal>
                <div data-testid="contact-success" className="border border-accent/40 bg-accent/5 p-10 text-center">
                  <CheckCircle2 className="mx-auto h-12 w-12 text-accent" />
                  <h2 className="mt-6 font-mincho text-2xl font-bold text-white">送信が完了しました</h2>
                  <p className="mt-4 text-sm leading-relaxed text-gray-400">
                    お問い合わせありがとうございます。内容を確認のうえ、担当者より折り返しご連絡いたします。
                  </p>
                  <button
                    data-testid="contact-reset-button"
                    onClick={() => {
                      setSent(false);
                      setForm(INITIAL);
                    }}
                    className="mt-8 border border-line px-6 py-3 font-display text-xs font-bold tracking-[0.2em] text-gray-300 transition-colors hover:border-accent hover:text-accent"
                  >
                    続けて別の問い合わせを送る
                  </button>
                </div>
              </Reveal>
            ) : (
              <Reveal>
                <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-6">
                  <label className="block">
                    <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">お問い合わせ種別 *</span>
                    <select data-testid="contact-category-select" value={form.category} onChange={update("category")} className={`mt-2 ${inputCls}`}>
                      {INQUIRY_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <label className="block">
                      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">お名前 *</span>
                      <input data-testid="contact-name-input" required value={form.name} onChange={update("name")} className={`mt-2 ${inputCls}`} placeholder="山田 太郎" />
                    </label>
                    <label className="block">
                      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">会社名・団体名</span>
                      <input data-testid="contact-company-input" value={form.company} onChange={update("company")} className={`mt-2 ${inputCls}`} placeholder="株式会社○○" />
                    </label>
                  </div>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <label className="block">
                      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">メールアドレス *</span>
                      <input data-testid="contact-email-input" type="email" required value={form.email} onChange={update("email")} className={`mt-2 ${inputCls}`} placeholder="you@example.jp" />
                    </label>
                    <label className="block">
                      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">電話番号</span>
                      <input data-testid="contact-phone-input" value={form.phone} onChange={update("phone")} className={`mt-2 ${inputCls}`} placeholder="059-000-0000" />
                    </label>
                  </div>
                  <label className="block">
                    <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">お問い合わせ内容 *</span>
                    <textarea
                      data-testid="contact-message-input"
                      required
                      rows={7}
                      value={form.message}
                      onChange={update("message")}
                      className={`mt-2 resize-y ${inputCls}`}
                      placeholder="ご相談内容、ご希望の時期・場所などをご記入ください。"
                    />
                  </label>
                  <button
                    data-testid="contact-submit-button"
                    type="submit"
                    disabled={sending}
                    className="group inline-flex items-center gap-3 bg-accent px-10 py-4 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50"
                  >
                    {sending ? "送信中..." : "送信する"}
                    <Send className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </button>
                </form>
              </Reveal>
            )}
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="border border-line bg-panel p-8">
                <p className="font-mono text-[10px] tracking-[0.35em] text-accent">OFFICE</p>
                <h2 className="mt-4 font-mincho text-xl font-bold text-white">{COMPANY.name}</h2>
                <div className="mt-8 space-y-6 text-sm text-gray-400">
                  <p className="flex items-start gap-4">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <span>
                      {contact.zip}
                      <br />
                      {contact.address}
                    </span>
                  </p>
                  <p className="flex items-start gap-4">
                    <Phone className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <span>
                      TEL {contact.tel}
                      <br />
                      FAX {contact.fax}
                    </span>
                  </p>
                  <p className="flex items-start gap-4">
                    <Clock className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <span>
                      営業時間 {contact.hours}
                      <br />
                      定休日 {contact.holiday}
                    </span>
                  </p>
                </div>
                <div className="mt-8 border-t border-line pt-6">
                  <p className="text-xs leading-relaxed text-gray-500">
                    お急ぎの場合はお電話にてお問い合わせください。フォームからのお問い合わせは、内容確認後、担当者より2営業日以内にご連絡いたします。
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
